<template>
  <div>
    <div
      class="text-center"
      v-if="!dataLoaded"
    >
      <v-progress-circular
        class="mt-6"
        color="primary"
        :size="50"
        indeterminate
      />
    </div>

    <div
      class="mt-4"
      v-show="dataLoaded"
    >
      <TitleCard
        :is-show-mode="isShowMode"
        :is-process-mode="isProcessMode"
        :is-edit-mode="isEditMode"
        :disabled="invalid"
        :saveLoading="saveBtnLoading"
        :selected-item.sync="grant"
        :is-no-changes="isNoChanges"
        @save="saveGrant"
      />

      <v-row
        class="mb-0 mt-2"
        style="margin-right: -14px"
      >
        <v-col
          ref="workList"
          cols="12"
          class="pt-1 pb-2 pr-2 overflow-auto"
        >
          <WorkTitleCard :title="workTypeRusName(workTypeName.Grant)" />

          <AuthorsCard
            class="mt-4"
            title="Автор"
            :selected-item.sync="grant.commonWork.authors"
            :is-show-mode="isShowMode"
            :max-authors="10"
            hide-share
            @updateInvalid="updateAuthorsInvalid"
          />

          <GrantCard
            class="mt-4"
            title="Основная информация"
            :linkFileList="linkFileList"
            :selected-item.sync="grant"
            :is-show-mode="isShowMode"
            @updateInvalid="updateGrantInvalid"
            @uploadLinkFiles="initUploadLinkFiles"
            @removeLinkFile="initRemoveLinkFile"
          />

          <CommentCard
            v-if="Boolean(grant.commonWork.comment)"
            class="mt-4"
            :comment="grant.commonWork.comment"
          />

          <CreatorCard :creator-name="grant.commonWork.creator.fullName" />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { grantApi } from '@/api'
import { Grant } from '../../models'
import GrantCard from '../../components/Works/Grant/GrantCard'
import TitleCard from '@/components/Works/elements/TitleCard'
import AuthorsCard from '../../components/Works/elements/AuthorsCard'
import CommentCard from '@/components/Works/elements/CommentCard.vue'
import { mapActions, mapGetters } from 'vuex'
import { workListHeight } from '@/mixins/workListHeight'
import { dictionaryNames, fileTypeId, workMode, workTypeName } from '@/scripts/consts'
import WorkTitleCard from '@/components/Works/elements/WorkTitleCard.vue'
import CreatorCard from '@/components/Works/elements/CreatorCard.vue'
import { filesForWork } from '@/mixins/filesForWork'

export default {
  name: 'GrantParent',
  components: {
    CreatorCard,
    WorkTitleCard,
    CommentCard,
    AuthorsCard,
    TitleCard,
    GrantCard
  },

  metaInfo() {
    return {
      title: this && this.workTypeRusName(workTypeName.Grant)
    }
  },

  mixins: [workListHeight, filesForWork],

  created() {
    this.isShowMode = this.$route.params.mode === workMode.Show
    this.isProcessMode = this.$route.params.mode === workMode.Process
    this.isEditMode = this.$route.params.mode === workMode.Edit
    this.getData()
  },

  data: () => ({
    grant: new Grant(),
    dataLoaded: false,
    isShowMode: false,
    isEditMode: false,
    isProcessMode: false,
    authorsInvalid: false,
    grantInvalid: false,
    saveBtnLoading: false,
    isNoChanges: true
  }),

  computed: {
    workTypeName() {
      return workTypeName
    },
    ...mapGetters('workTypes', ['workTypeByName', 'workTypeRusName']),

    invalid() {
      return this.authorsInvalid || this.grantInvalid
    }
  },

  methods: {
    ...mapActions('dictionaries', ['getDictionaries']),
    ...mapActions('workTypes', ['getWorkTypes']),

    async getData() {
      if (!this.isShowMode)
        await this.getDictionaries([
          dictionaryNames.NirType,
          dictionaryNames.FundingRecipient,
          dictionaryNames.ScientDepartment,
          dictionaryNames.NirLevel
        ])
      await this.getWorkTypes()
      await this.getGrant()
      this.dataLoaded = true
      this.isNoChanges = true
    },

    async getGrant() {
      try {
        if (this.$route.params?.id !== '0') {
          const data = await grantApi.get(this.$route.params?.id)
          await this.getFileList(data.commonWork?.id)
          this.grant = new Grant(data)
        }
      } catch (e) {
        console.log(e)
        this.showErrorMessage(e, 'Не удалось получить данные по работе')
      }
    },

    async saveGrant(isThrowError = false) {
      try {
        this.saveBtnLoading = true

        if (!this.grant.id) this.grant.commonWork.workType = this.workTypeByName(workTypeName.Grant)

        const body = this.grant.getForAPI()

        const data = this.grant.id ? await grantApi.update(body) : await grantApi.create(body)

        await this.removeFileFromAPI()

        const linkFileListForSave = this.linkFileList.filter(file => !file.id)

        if (linkFileListForSave.length) await this.saveFile(data?.commonWork?.id, linkFileListForSave, fileTypeId.link)

        if (!this.grant.id) await this.$router.replace('/works/grant/edit/' + data.id)

        await this.getFileList(data.commonWork?.id)
        this.grant = new Grant(data)
        this.$toast.info('Работа успешно сохранена')
        queueMicrotask(() => (this.isNoChanges = true))
      } catch (e) {
        console.log(e)
        if (e.message === 'Ошибка удаления файлов') this.getFileList(this.grant.commonWork?.id)
        else this.showErrorMessage(e, 'Ошибка сохранения работы')
        if (isThrowError) throw new Error('Ошибка сохранения')
      } finally {
        this.saveBtnLoading = false
      }
    },

    initUploadLinkFiles(files) {
      this.uploadLinkFiles(files)
      this.isNoChanges = false
    },

    initRemoveLinkFile(index) {
      this.removeLinkFile(index)
      this.isNoChanges = false
    },

    async updateAuthorsInvalid(val) {
      this.authorsInvalid = val
      this.isNoChanges = false
    },

    async updateGrantInvalid(val) {
      this.grantInvalid = val
      this.isNoChanges = false
    }
  },

  watch: {
    dataLoaded() {
      this.setWorkListHeight()
    }
  },

  provide: function () {
    return {
      saveWork: this.saveGrant
    }
  }
}
</script>

<style scoped></style>
